<template>
    <div class="profile-page">
        <h2>마이페이지</h2>
        <div class="profile-form-box">
            <div class="profile-form">
                <div class="horizontal-row">
                    <label>이름</label>
                    <input type="text" v-model="name" :readonly="!isEditing" @keydown.space.prevent/>
                </div>
                <div class="horizontal-row">
                    <label>이메일</label>
                    <input type="email" v-model="email" :readonly="true" @keydown.space.prevent/>
                </div>

                <div class="horizontal-row">
                    <label>비밀번호</label>
                    <input type="password" v-model="password" :readonly="!isEditing" placeholder="영문, 숫자, 특수문자를 포함하여 8자 이상 입력하세요" @input="filterPassword" @keydown.space.prevent/>
                </div>

                <div class="horizontal-row">
                    <label>비밀번호 확인</label>
                    <input type="password" v-model="passwordCheck" :readonly="!isEditing" placeholder="영문, 숫자, 특수문자를 포함하여 8자 이상 입력하세요" @input="filterPasswordCheck" @keydown.space.prevent/>
                </div>

                <div class="horizontal-row">
                    <label>전화번호</label>
                    <input type="text" v-model="phone" :readonly="!isEditing" @input="filterPhone"/>
                </div>

            </div>
            <button class="edit-btn" @click="editProfile">
                {{ isEditing ? '저장' : '수정' }}
            </button>
        </div>
    </div>
</template>

<script>
import api, {urls} from "@/api";
import { useAuthStore } from '@/stores/auth';

export default {
    meta: {
        title: "마이페이지"
    },
    data() {
        return {
            name: '',
            email: '',
            password: '',
            passwordCheck: '',
            phone: '',
            isEditing: false,  // 편집 모드 상태
        };
    },
    methods: {
        formatPhoneNumber(phone) {
            if (!phone) return '';
            const cleaned = phone.replace(/\D/g, '');  // 숫자만 남김
            if (cleaned.length === 11) {
                return cleaned.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
            } else if (cleaned.length === 10) {
                return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
            }
            return phone; // 포맷 못하면 원본 반환
        },
        async loadAdminProfile() {
            const token = localStorage.getItem('jwtToken');
            if (!token){
                const authStore = useAuthStore();

                this.name = authStore.getUser?.name || '';
                this.email = authStore.getUser?.email || '';
                this.phone = this.formatPhoneNumber(authStore.getUser?.phone || '');
                this.password = '';        // 비밀번호는 보통 빈 값으로 초기화
                this.passwordCheck = '';   // 비밀번호 확인도 초기화
                return;
            }

            // 토큰 디코딩 함수 (예시)
            const userInfo = this.parseJwt(token);
            if (!userInfo || !userInfo.sub) return;

            try {
                const res = await api.get(urls.mypage.mypageData, {
                    params: { user_email: userInfo.sub }
                });
                const data = res.data.list[0];
                if (data) {
                    this.name = data.name || '';
                    this.email = data.email || '';
                    this.phone = this.formatPhoneNumber(data.phone || '');
                    this.password = '';        // 비밀번호는 보통 빈 값으로 초기화
                    this.passwordCheck = '';   // 비밀번호 확인도 초기화
                }
            } catch(error) {
                console.error('관리자 정보 조회 실패', error);
                alert('프로필 정보를 불러오는데 실패했습니다.');
            }
        },
        async editProfile() {
            if (this.isEditing) {
                const isSave = window.confirm('수정하신 정보를 저장하시겠습니까?');
                if (!isSave) {
                    return;
                }

                // 이메일 형식 검사
                if (!this.isValidEmail(this.email)) {
                    alert('올바른 이메일 형식을 입력해주세요.');
                    return;
                }

                // 비밀번호와 비밀번호 확인이 모두 입력된 경우에만 비밀번호 검사 수행
                if (this.password || this.passwordCheck) {
                    if (this.password !== this.passwordCheck) {
                        alert('비밀번호와 비밀번호 확인이 일치하지 않습니다.');
                        return;
                    }
                    if (!this.isValidPassword(this.password)) {
                        alert('비밀번호는 영문, 숫자, 특수문자를 포함하여 8자 이상이어야 합니다.');
                        return;
                    }
                }

                const token = localStorage.getItem('jwtToken');
                const userInfo = this.parseJwt(token);

                const authStore = useAuthStore();
                const requestData = {
                    name: this.name,
                    phone: this.phone.replace(/-/g, ''),
                    email: token ? this.email : authStore.getUser?.email,
                    user_email: token ? userInfo.sub : authStore.getUser?.email
                };
                // 비밀번호와 비밀번호 확인이 모두 입력된 경우에만 비밀번호 업데이트 로직 실행
                if (this.password && this.passwordCheck) {
                    if (this.password !== this.passwordCheck) {
                        alert('비밀번호와 비밀번호 확인이 일치하지 않습니다.');
                        return;
                    }
                    // 비밀번호가 일치하면 요청 데이터에 추가
                    requestData.password = this.password;
                }
                try {
                    const res = await api.post(urls.mypage.modify, requestData);

                    if (res.data.success) {
                        alert('프로필이 성공적으로 수정되었습니다.');
                        if (requestData.password) {
                            this.password = '';
                            this.passwordCheck = '';
                        }
                    } else {
                        alert('프로필 수정에 실패했습니다.');
                    }
                } catch (error) {
                    console.error('관리자 정보 수정 실패', error);
                    alert('프로필 정보를 수정하는데 실패했습니다.');
                }
                return;
            }

            const isEdit = window.confirm('내 정보를 수정하시겠습니까?');
            if (!isEdit) {
                // 편집 취소
                return;
            }
            this.isEditing = true;

        },

        parseJwt(token) {
            try {
                const base64Url = token.split('.')[1];
                const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
                const jsonPayload = decodeURIComponent(
                    atob(base64)
                        .split('')
                        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                        .join('')
                );
                return JSON.parse(jsonPayload);
            } catch (e) {
                console.error('토큰 디코딩 실패', e);
                return null;
            }
        },
        // 이메일 형식 검사 정규식
        isValidEmail(email) {
            const emailRegex = /^[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
            return emailRegex.test(email);
        },
        // 비밀번호 형식 검사 정규식 (영문, 숫자, 특수문자 포함, 8자 이상)
        isValidPassword(password) {
            if (!password) return true; // 비밀번호 미입력 시 검사 통과 (수정 시 선택 사항)
            const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[~!@#%^&*_-])[A-Za-z\d~!@#%^&*_-]{8,}$/
            return passwordRegex.test(password);
        },
        filterPassword(e) {
            e.target.value = e.target.value.replace(/[ㄱ-ㅎ가-힣\s]/g, '');
            this.password = e.target.value;
        },
        filterPasswordCheck(e) {
            e.target.value = e.target.value.replace(/[ㄱ-ㅎ가-힣\s]/g, '');
            this.passwordCheck = e.target.value;
        },
        filterPhone(e) {
            // 숫자만 입력, 11글자까지만
            let onlyNum = e.target.value.replace(/[^0-9]/g, '');
            if (onlyNum.length > 11) {
                onlyNum = onlyNum.slice(0, 11);
            }
            e.target.value = onlyNum;
            this.phone = onlyNum;
        },


    },
    mounted() {
        this.loadAdminProfile();
    },
}
</script>

<style scoped>
.profile-page {margin: 36px 0 0 36px;}
.profile-form-box {display: flex;align-items: flex-start;}
.profile-form {display: flex;flex-direction: column;gap: 18px;margin-right: 24px;min-width: 600px;}
.profile-form label {font-size: 1rem;margin-bottom: 5px;color: #555;}
.profile-form input {padding: 7px 13px;border: 1.7px solid #ccc;border-radius: 7px;font-size: 1rem;margin-bottom: 8px;}

.horizontal-row {display: flex;align-items: center;gap: 18px;}
.horizontal-row label {width: 105px;color: #555;font-size: 1rem;margin-bottom: 0;flex-shrink: 0;}
.horizontal-row input {flex: 1;padding: 10px 16px;border: 1.7px solid #ccc;border-radius: 7px;font-size: 1rem;min-width: 0;}

.edit-btn {background: #1867c0;color: #fff;border: none;border-radius: 25px;padding: 10px 32px;font-weight: 600;font-size: 1.03rem;cursor: pointer;margin-left:236px;height: 44px;align-self: flex-start;box-shadow: 0 2px 6px rgba(15,41,97,0.06);}
.edit-btn:hover {background: #145095;}
</style>
